/*
 * Copyright (C) 2017 Spanning Cloud Apps.  All rights reserved.
 */
import './metadataSelectView.tpl.html';
import * as _l from 'lodash';

angular
  .module('bulkRestore.metadataSelectView', [
    'services.bulkrestore',
    'services.util',
    'shared.metadataViewItemModal',
    'shared.filters'
  ])
  /**
   * Controller for the bulkRestore.metadataSelectView template.
   */
  .controller('BulkRestoreMetadataSelectViewCtrl', [
    '$filter',
    '$uibModal',
    '$q',
    '$rootScope',
    '$scope',
    '$state',
    '$timeout',
    'httpQueue',
    'VIEW_STATES',
    'ErrorService',
    'progressFactory',
    'accountUtils',
    'batchPicker',
    'batchUtils',
    'httpMetadata',
    'BulkRestoreService',
    'BulkRestoreType',
    'UtilService',
    'WizardService',
    'ALERT_EVENTS',
    function (
      $filter,
      $uibModal,
      $q,
      $rootScope,
      $scope,
      $state,
      $timeout,
      httpQueue,
      VIEW_STATES,
      ErrorService,
      progressFactory,
      accountUtils,
      batchPicker, // Resolved in restore.js
      batchUtils,
      httpMetadata,
      BulkRestoreService,
      BulkRestoreType,
      UtilService,
      WizardService,
      ALERT_EVENTS
    ) {
      if (!$state.params.bulkRestoreData) {
        throw new Error('bulkRestoreData is required');
      }

      var i18n = $filter('i18n');
      var pluralize = $filter('pluralize');
      var momentFilter = $filter('momentFilter');
      var bulkRestoreData = $state.params.bulkRestoreData;
      // We won't show text file contents over this size. We will never show binary file contents.
      var MAX_DISPLAYED_LINES = 4000;
      var momentStr;
      var touched = true;

      // Publish functions in scope
      $scope.getExpanderClass = getExpanderClass;
      $scope.invalidDate = invalidDate;
      $scope.loadItemsForType = loadItemsForType;
      $scope.previewItem = previewItem;
      $scope.toggleAllTypesSelection = toggleAllTypesSelection;
      $scope.toggleItemSelection = toggleItemSelection;
      $scope.toggleTypeSelection = toggleTypeSelection;
      $scope.toggleTypeExpansion = toggleTypeExpansion;
      $scope.hasEnoughBackups = hasEnoughBackups;
      $scope.submitBackup = submitBackup;

      // Initialize scope variables
      $scope.bulkRestoreData = bulkRestoreData;

      // If we're coming from a compare we don't show the batch picker.
      if (bulkRestoreData.isRestoreFromMetadataCompare()) {
        momentStr = momentFilter(bulkRestoreData.getBackupBatchDate(), 'M_D_Y_H_M');
        $scope.restoreItemsMsg = i18n('RESTORE_ITEMS_FROM_BACKUP_ON_DATE', momentStr);

        // Load the batch if we don't already have it loaded.
        if (bulkRestoreData.getMetadataRecords().length === 0) {
          batchChanged(bulkRestoreData.getCompareBatchId());
        } else {
          // We need to count the selected items if we're redisplaying a batch.
          initForNewBatch();
        }
      } else {
        $scope.batchPicker = batchPicker;
        watchBatchPicker();

        $rootScope.showLoadingMask = true;
        const backupBatchId = bulkRestoreData.getBackupBatchId();
        if (backupBatchId) {
          // If there is already a backup batch id that means we're coming back to this page from using Next/Prev. We
          // need to load that batch into the picker.
          $scope.batchPicker.getBatch(backupBatchId);
        } else {
          // If there is not already a backup batch id get the latest.
          $scope.batchPicker.getLatest();
        }
      }

      initWizardPage();
      watchRequestsInProgress();

      /**
       * Sets up this page for use with the wizard service.
       */
      function initWizardPage() {
        var page = WizardService.getCurrentPage();
        page.setHeaderText(i18n('SELECT_RESTORE_ITEMS'));
        page.setStateParams($state.params);
        page.setValid(isValid);
        // For the purposes of confirming cancellation of the wizard we'll consider the wizard touched if we get this
        // far.
        page.setTouched(() => touched);
      }

      /**
       * Watches for the selected batch to change.
       */
      function watchBatchPicker() {
        $scope.$watch(
          function () {
            return $scope.batchPicker.batchObject.batchId;
          },
          function (newVal) {
            if (newVal && newVal !== bulkRestoreData.getBackupBatchId()) {
              bulkRestoreData.setBackupBatchId(newVal);
              bulkRestoreData.setBackupBatchDate($scope.batchPicker.batchObject.date);

              batchChanged(newVal);
            }
          }
        );
      }

      /**
       * Determines whether there are enough backups to run a restore.
       *
       * @returns {boolean} true if there are enough backups.
       */
      function hasEnoughBackups() {
        return batchUtils.backupBatchDots.count > 0;
      }

      /**
       * Submit a backup to be queued.
       */
      function submitBackup() {
        $rootScope.showLoadingMask = true;

        return httpQueue
          .submitBackup()
          .then(() => {
            progressFactory.backupInProgress = true;

            // Reset the touched flag so we can go to the dashboard without confirmation
            touched = false;
            $state.go(VIEW_STATES.ADMIN_HOME);
          })
          .catch((err) => {
            ErrorService.report(err, i18n('SUBMIT_BACKUP_FAILED'));
            progressFactory.backupInProgress = false;
          })
          .finally(() => {
            $rootScope.showLoadingMask = false;
          });
      }

      /**
       * Watches for a change to the number of server requests currently in progress.
       */
      function watchRequestsInProgress() {
        $scope.$watch(
          function () {
            return batchUtils.requestsInProgress;
          },
          function (newVal, oldVal) {
            if (newVal !== oldVal) {
              $rootScope.showLoadingMask = newVal > 0;
            }
          }
        );
      }

      /**
       * Determines whether the settings on the page are valid (i.e. Next is allowed in the wizard)
       *
       * @returns {boolean} true if the settings are valid, false otherwise.
       */
      function isValid() {
        // We just need a selection
        return bulkRestoreData.getSelectedMetadataRecordCount() > 0;
      }

      /**
       * Returns the class to be used to style a component type expander
       *
       * @param {Object} typeRecord - the component type record
       * @returns {String} - the CSS class(es)
       */
      function getExpanderClass(typeRecord) {
        var dirClass = typeRecord.isExpanded ? 'sbsf-log-header-asc' : 'sbsf-log-header-desc';
        return dirClass + ' sca-icon-down-black';
      }

      /**
       * Utility to emit an error if an invalid date is selected.
       */
      function invalidDate() {
        $scope.batchPicker.invalidSelectedDate = true;
        $rootScope.$emit(ALERT_EVENTS.ERROR.eventName, i18n('INVALID_DATE'));

        // Clear the highlight after 10 seconds
        $timeout(function () {
          $scope.batchPicker.invalidSelectedDate = false;
        }, 10000);
      }

      /**
       * Toggles the expanded state for a component type record
       *
       * @param {Object} type - the component type record
       * @returns {Promise} - a promise that is resolved when the item records are loaded
       */
      function toggleTypeExpansion(type) {
        type.isExpanded = !type.isExpanded;

        // Lazy load the metadata items if expanded
        return type.isExpanded ? loadItemsForType(type) : $q.when();
      }

      /**
       * Select the meta.xml file when a non-meta.xml path is selected
       *
       * @param {Object} type - the component type record
       * @param {Object} myItem - the item record selected
       */
      function selectMetaForItem(type, myItem) {
        var pathWithMeta = myItem.path + '-meta.xml';

        // Only update the meta item if the selected item is not a meta and the change is to select it.
        if (!myItem.path.endsWith('-meta.xml') && myItem.isSelected) {
          type.items.forEach(function (item) {
            if (item.path === pathWithMeta) {
              item.isSelected = true;
            }
          });
        }
      }

      /**
       * Toggles the selected state for a metadata item
       *
       * @param {Object} type - the component type record containing the item
       * @param {Object} item - the item record
       * @param {Event} event - the click event
       */
      function toggleItemSelection(type, item, event) {
        // We only allow this if the type is not selected
        if (!type.isSelected) {
          item.isSelected = !item.isSelected;

          //Select a corresponding meta
          selectMetaForItem(type, item);

          // If all items in this component type are now selected, select the component type.
          // If not all items are selected, unselect the component type.
          type.selectedItemCount = getSelectedItemCount(type.items);
          type.isSelected = type.selectedItemCount === type.total;

          calculateItemsSelected();
          updateAllTypesSelection();
        }

        // Don't allow the click to flow to the parent container since that would cause a preview
        event.stopPropagation();
      }

      /**
       * Toggles the selected state for a component type record
       *
       * @param {Object} type - the component type record
       * @returns {Promise} - a promise that is resolved when the selection completes
       */
      function toggleTypeSelection(type) {
        var promise;

        type.isSelected = !type.isSelected;

        // Load the metadata items on selection so they are cached for the Restore step
        if (type.isSelected && !type.items) {
          promise = loadItemsForType(type);
        } else {
          updateItemSelectionForType(type);
          promise = $q.when();
        }

        return promise.then(updateAllTypesSelection);
      }

      /**
       * Updates the metadata item selection states to match the selection state of the type
       *
       * @param {Object} type - the component type record
       */
      function updateItemSelectionForType(type) {
        // Reflect the new selected state in all the child items
        _.each(type.items, function (item) {
          item.isSelected = type.isSelected;
        });

        // At this point either all items are selected or none are
        type.selectedItemCount = type.isSelected ? type.total : 0;

        calculateItemsSelected();
      }

      /**
       * Toggles the selected state for all component type records
       *
       * @returns {Promise} - a promise that is resolved when the selection completes
       */
      function toggleAllTypesSelection() {
        var promises;

        bulkRestoreData.setSelectingAll(!bulkRestoreData.isSelectingAll());

        // Load the metadata items on selection so they are cached for the Restore step
        if (bulkRestoreData.isSelectingAll()) {
          promises = _.map(bulkRestoreData.getMetadataRecords(), function (type) {
            if (!type.isSelected) {
              return toggleTypeSelection(type);
            }
          });
          return $q.all(promises);
        } else {
          _.each(bulkRestoreData.getMetadataRecords(), function (type) {
            type.isSelected = false;
            updateItemSelectionForType(type);
          });
          return $q.when();
        }
      }

      /**
       * Called when a new batch has been selected
       *
       * @param {Number} batchId - the new batch
       * @returns {Promise} a promise that resolves when the component type totals have been fetched.
       */
      function batchChanged(batchId) {
        $rootScope.showSecondaryLoadingMask = true;
        return BulkRestoreService.getMetadataComponentTypeTotals(batchId)
          .then((results) => {
            // Sort the results by component type
            bulkRestoreData.setMetadataRecords(_.sortBy(_l.cloneDeep(results), 'componentType'));

            initForNewBatch();
          })
          .finally(() => {
            $rootScope.showSecondaryLoadingMask = false;
          });
      }

      /**
       * Sets up the counts and selections when we're displaying a batch for the first time.
       */
      function initForNewBatch() {
        initSelectedItemCounts();
        calculateItemsFound();
        calculateItemsSelected();
        updateAllTypesSelection();
      }

      /**
       * Sets up the selected item counts in the metadata records, if necessary.
       */
      function initSelectedItemCounts() {
        _.each(bulkRestoreData.getMetadataRecords(), function (type) {
          // If it came from the cache it will already be set and we don't want to initialize it
          if (type.selectedItemCount === undefined) {
            type.selectedItemCount = 0;
          }

          // On batch load we want to expand any types that are not selected but have selected items
          if (!type.isSelected && type.selectedItemCount > 0) {
            type.isExpanded = true;
          }
        });
      }

      /**
       * Loads the metadata items for the a component type
       *
       * @param {Object} type - the component type record
       * @returns {Promise} - a promise that is resolved when the item records are loaded
       */
      function loadItemsForType(type) {
        if (type.items) {
          // Already loaded
          return $q.when();
        }

        return BulkRestoreService.getMetadataForBatchAndComponentType(
          bulkRestoreData.isRestoreFromMetadataCompare()
            ? bulkRestoreData.getCompareBatchId()
            : bulkRestoreData.getBackupBatchId(),
          type.componentType
        ).then(function (items) {
          type.items = _l.cloneDeep(items);

          if (type.isSelected) {
            // Reflect this state in all the child items
            updateItemSelectionForType(type);
          }
        });
      }

      /**
       * Called to compute the sum of the totals of the displayed component types
       */
      function calculateItemsFound() {
        var sum = _.reduce(
          bulkRestoreData.getMetadataRecords(),
          function (memo, type) {
            return memo + type.total;
          },
          /*initialMemo:*/ 0
        );
        $scope.itemsFoundMsg = pluralize(sum, 'ITEM_FOUND', 'ITEMS_FOUND');
      }

      /**
       * Counts the number of selected types and updates the state of the 'all types' checkbox, if necessary.
       */
      function updateAllTypesSelection() {
        var selectedTypeCount = getSelectedTypeCount();
        bulkRestoreData.setSelectingAll(selectedTypeCount === bulkRestoreData.getMetadataRecords().length);
      }

      /**
       * Called to compute the number of selected metadata items
       */
      function calculateItemsSelected() {
        var count = 0;

        _.each(bulkRestoreData.getMetadataRecords(), function (type) {
          // Default to 0 if selectedItemCount is undefined
          count += type.selectedItemCount || 0;
        });

        bulkRestoreData.setSelectedMetadataRecordCount(count);
        $scope.itemsSelectedMsg = i18n('SELECTED', count);
      }

      /**
       * Returns the number of selected items in a list of metadata items.
       *
       * @param {Array} items - the items to be examined
       * @returns {Number} - The number of selected items.
       */
      function getSelectedItemCount(items) {
        var count = 0;

        _.each(items, function (item) {
          if (item.isSelected) {
            count++;
          }
        });

        return count;
      }

      /**
       * Returns the number of selected component types.
       *
       * @returns {Number} - The number of selected types.
       */
      function getSelectedTypeCount() {
        var count = 0;

        _.each(bulkRestoreData.getMetadataRecords(), function (type) {
          if (type.isSelected) {
            count++;
          }
        });

        return count;
      }

      /**
       * Displays a modal showing the contents of a metadata item.
       *
       * @param {Object} item - the metadata item record
       * @param {Event} event - the click event
       */
      function previewItem(item, event) {
        $rootScope.showLoadingMask = true;

        $uibModal
          .open({
            templateUrl: 'shared/modal/metadataViewItemModal.tpl.html',
            controller: 'MetadataViewItemModalCtrl',
            windowClass: 'sbsf-metadata-view-item-modal-window',
            backdrop: 'static',
            resolve: {
              orgInfo: function () {
                return accountUtils.getOrgInfo();
              },
              item: function () {
                item.date = bulkRestoreData.getBackupBatchDate();
                return item;
              },
              fileInfo: function () {
                return httpMetadata.getItemFileInfo({
                  batchId: bulkRestoreData.getBackupBatchId(),
                  metadataPath: item.path,
                  maxLineCount: MAX_DISPLAYED_LINES,
                  discardBinary: true
                });
              }
            }
          })
          .opened.then(function () {
            // this is a total hack to snap to the top of the screen when the modal is opened.
            UtilService.scrollToElement('sbsf-main-ctr');

            $rootScope.showLoadingMask = false;
          });

        // Don't allow the click to flow to the parent container since that might cause another preview
        event.stopPropagation();
      }
    }
  ]);
